import {
  Component,
  Input,
  EventEmitter,
  Output,
  OnChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FieldType, FormFieldI, FormPayloadI, ValidatorsI } from '../../types';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { get, isArray, set } from 'lodash-es';
import { TextPrettyPipe } from 'src/app/pipes/text-pretty.pipe';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-form-buider',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatInputModule,
    MatSlideToggleModule,
    TextPrettyPipe,
    MatButtonModule,
    MatIconModule,
    MatSelectModule,
  ],
  template: `
    <h2 *ngIf="FormPayload.title">{{ FormPayload.title }}</h2>
    <form
      [formGroup]="FormG"
      *ngIf="FormG"
      (ngSubmit)="(SubmitAction)"
      (click)="console.debug(JSON.stringify(generateOutput()))"
    >
      <ng-container *ngFor="let field of FormPayload.fields">
        <ng-container
          *ngTemplateOutlet="Field; context: { field }"
        ></ng-container>
        <br />
      </ng-container>
      <ng-template #Field let-field="field">
        <!-- Frontend-only secret masking: render secret-keyed fields as masked
             password inputs regardless of declared type. No template/DB change. -->
        <ng-container *ngIf="isSecret(field); else byType">
          <ng-container *ngTemplateOutlet="PASSWORD_FIELD; context: { field }"></ng-container>
        </ng-container>
        <ng-template #byType>
        <ng-container [ngSwitch]="field.type">
          <ng-container *ngSwitchCase="undefined">
            <ng-container
              *ngTemplateOutlet="TEXT_FIELD; context: { field }"
            ></ng-container>
          </ng-container>
          <ng-container *ngSwitchCase="FieldType.TEXT">
            <ng-container
              *ngTemplateOutlet="TEXT_FIELD; context: { field }"
            ></ng-container>
          </ng-container>
          <ng-container *ngSwitchCase="FieldType.MULTILINE_TEXT">
            <ng-container
              *ngTemplateOutlet="MULTILINE_TEXT_FIELD; context: { field }"
            ></ng-container>
          </ng-container>
          <ng-container *ngSwitchCase="FieldType.PASS">
            <ng-container
              *ngTemplateOutlet="PASSWORD_FIELD; context: { field }"
            ></ng-container>
          </ng-container>
          <ng-container *ngSwitchCase="FieldType.STRING_LIST">
            <ng-container
              *ngTemplateOutlet="TEXT_FIELD; context: { field }"
            ></ng-container>
          </ng-container>

          <ng-container *ngSwitchCase="FieldType.NUM">
            <ng-container
              *ngTemplateOutlet="NUMBER_FIELD; context: { field }"
            ></ng-container>
          </ng-container>

          <ng-container *ngSwitchCase="FieldType.DROPDOWN">
            <ng-container
              *ngTemplateOutlet="DROPDOWN_FIELD; context: { field }"
            ></ng-container>
          </ng-container>

          <ng-container *ngSwitchDefault>
            <p><span class="text-red-500">Failed to Render!</span></p>
            <p>Field: {{ JSON.stringify(field || {}) }}</p>
          </ng-container>
        </ng-container>
        </ng-template>
      </ng-template>

      <ng-template #TEXT_FIELD let-field="field">
        <mat-form-field
          appearance="fill"
          class="kate-mat-input"
          [ngStyle]="{
            width: fieldWidthInRem ? fieldWidthInRem + 'rem' : '22rem'
          }"
          *ngIf="!field.hidden"
        >
          <mat-label>{{
            field.label || (field.key | textpretty | titlecase)
          }}</mat-label>
          <input
            type="text"
            [readonly]="field.editable === false ? true : false"
            formControlName="{{ field.key }}"
            placeholder="{{field?.placeholder}}"
            matInput
          />
        </mat-form-field>
      </ng-template>

      <ng-template #MULTILINE_TEXT_FIELD let-field="field">
        <mat-form-field
          [ngStyle]="{
            width: fieldWidthInRem ? fieldWidthInRem + 'rem' : '22rem'
          }"
          *ngIf="!field.hidden"
        >
          <mat-label>{{
            field.label || (field.key | textpretty | titlecase)
          }}</mat-label>
          <textarea
            matInput
            appearance="fill"
            cdkTextareaAutosize
            cdkAutosizeMinRows="1"
            [readonly]="field.editable === false ? true : false"
            formControlName="{{ field.key }}"
          ></textarea>
        </mat-form-field>
      </ng-template>

      <ng-template #PASSWORD_FIELD let-field="field">
        <mat-form-field
          appearance="fill"
          class="kate-mat-input"
          [ngStyle]="{
            width: fieldWidthInRem ? fieldWidthInRem + 'rem' : '22rem'
          }"
          *ngIf="!field.hidden"
        >
          <mat-label>{{
            field.label || (field.key | textpretty | titlecase)
          }}</mat-label>
          <input
            [type]="pwVisible[field.key] ? 'text' : 'password'"
            [readonly]="field.editable === false ? true : false"
            formControlName="{{ field.key }}"
            matInput
          />
          <button
            type="button"
            mat-icon-button
            matSuffix
            tabindex="-1"
            [attr.aria-label]="pwVisible[field.key] ? 'Hide value' : 'Show value'"
            (click)="pwVisible[field.key] = !pwVisible[field.key]"
          >
            <mat-icon>{{ pwVisible[field.key] ? 'visibility_off' : 'visibility' }}</mat-icon>
          </button>
        </mat-form-field>
      </ng-template>
      <ng-template #NUMBER_FIELD let-field="field">
        <mat-form-field
          appearance="fill"
          [ngStyle]="{
            width: fieldWidthInRem ? fieldWidthInRem + 'rem' : '22rem'
          }"
          class=""
        >
          <mat-label>{{
            field.label || (field.key | textpretty | titlecase)
          }}</mat-label>
          <input
            type="number"
            formControlName="{{ field.key }}"
            [required]="field.validators?.required || false"
            matInput
          />
        </mat-form-field>
      </ng-template>

      <ng-template #DROPDOWN_FIELD let-field="field">
        <mat-form-field
          appearance="fill"
          class="kate-mat-input"
          [ngStyle]="{
            width: fieldWidthInRem ? fieldWidthInRem + 'rem' : '22rem'
          }"
          *ngIf="!field.hidden"
        >
          <mat-label>{{
            field.label || (field.key | textpretty | titlecase)
          }}</mat-label>
          <mat-select formControlName="{{ field.key }}">
            <mat-option *ngFor="let entity of field.options" [value]="entity">{{
              entity
            }}</mat-option>
          </mat-select>
        </mat-form-field>
      </ng-template>
    </form>
    <!-- <actions-builder
      [ActionsPayload]="FormPayload.actions || []"
    ></actions-builder> -->
  `,
  styleUrls: ['./form-buider.component.scss'],
})
export class FormBuilderComponent implements OnChanges {
  FieldType = FieldType;
  // Per-field show/hide state for password inputs (eye toggle)
  pwVisible: { [key: string]: boolean } = {};

  // Frontend-only: treat a field as a secret (masked) when its declared type is
  // password OR its key looks secret-like. No DB/connection-template change.
  // Matches 'credentials', 'aws_secret_access_key', etc. but NOT 'aws_access_key_id'.
  isSecret(field: any): boolean {
    return (
      field?.type === FieldType.PASS ||
      /secret|password|credential|token|private[_-]?key/i.test(field?.key || '')
    );
  }
  JSON = JSON;
  console = console;
  @Input() FormPayload: FormPayloadI = {
    title: 'Default Form',
    fields: [],
    actions: [],
  };
  @Input() Output: any = {};
  @Input() fieldWidthInRem: number = 0;
  @Output() dataEvent = new EventEmitter<Output>();
  FormG?: FormGroup;
  Validators: { [k: string]: (payload: any) => any } = {
    min: (min: number) => {
      return Validators.min(min);
    },
    max: (max: number) => {
      return Validators.max(max);
    },
    email: () => {
      return Validators.email;
    },
    required: (required: boolean) => {
      return required ? Validators.required : () => true;
    },
  };
  @Input() SubmitAction: (formOutput: any) => void = (payload: any) =>
    undefined;
  constructor(private builder: FormBuilder) {}
  ngOnChanges(): void {
    this.FormG = this.builder.group(this.Controls());
    this.FormG?.patchValue(this.convertToFormPatch(this.Output));
  }
  Controls = () => {
    const controls: { [k: string]: any } = {};
    for (const field of this.FormPayload.fields) {
      controls[field.key] = this.builder.control(field.default, {
        validators: this.getValidators(field),
      });
    }
    return controls;
  };
  setDefaultTypes() {
    let i = 0;
    for (const field of this.FormPayload.fields) {
      if (!field.type) {
        this.FormPayload.fields[i].type = FieldType.TEXT;
      }
      i++;
    }
  }
  getValidators(field: FormFieldI) {
    const validators = [];
    const Vs: ValidatorsI = field.validators ?? {};
    for (const validator in Vs) {
      validators.push(this.Validators[validator].apply(this, [Vs[validator]]));
    }
    return validators;
  }
  convertToFormPatch(payload: any): any {
    const patch: any = {};
    const val = payload ?? {};
    for (const field of this.FormPayload.fields) {
      let value = get(val, field.key);
      if (value === undefined) {
        value = field.default;
      } else if (field.type === FieldType.STRING_LIST) {
        value = value.join(',');
      }
      patch[field.key] = value;
    }
    return patch;
  }

  generateOutput(): any {
    this.Output = {};
    const val = this.FormG?.value ?? {};
    for (const field of this.FormPayload.fields) {
      let value: any = val[field.key];
      if (value === '') value = null;
      if (field.type === FieldType.STRING_LIST) {
        try {
          value = value.split(',');
          value = value?.map((v: string) => v.trim());
        } catch {
          if (!isArray(value)) value = null;
        }
      }
      if (value != null) set(this.Output, field.key, value);
    }
    this.sendoutput(this.Output);
    return this.Output;
  }

  validateForm() {
    if (this.FormG?.valid) {
      this.generateOutput();
    } else {
      const temp: any = { invalid: true, ...this.Output };
      this.dataEvent.emit(temp);
    }
  }
  sendoutput(data: any) {
    this.dataEvent.emit(data);
  }
}
